import type { OAuthAuthorizationQuery } from '@fitness/api-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { ExternalLink, ShieldCheck, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ErrorBanner } from '@/components/ErrorBanner';
import { Spinner } from '@/components/Spinner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { OAUTH, oauthErrorMessage } from '@/lib/i18n/oauth';
import {
  AUTHORIZE_PATH,
  clearAuthorizationRequest,
  decisionBody,
  isFramed,
  parseAuthorizationRequest,
  pendingAuthorizationRequest,
} from '@/lib/oauthRequest';
import { HOME_ROUTE } from '@/lib/redirect';

/**
 * Pantalla de consentimiento OAuth (el `authorization_endpoint` del servidor
 * MCP remoto). Un cliente como el conector de claude.ai manda aquí al
 * navegador; la persona, con su sesión normal de la app, decide si le deja
 * entrar en su cuenta, y la API valida y emite el código.
 *
 * Va fuera del layout `_app`: no es una página de la app, sino una parada de
 * paso que termina siempre fuera de ella.
 */
export const Route = createFileRoute('/oauth/authorize')({
  beforeLoad: ({ context }) => {
    if (context.auth.status !== 'authenticated') {
      // Se conserva la petición completa. La pantalla, al volver, la lee de la
      // copia en crudo que guardó main.tsx (ver lib/oauthRequest.ts).
      throw redirect({
        to: '/login',
        search: { redirect: AUTHORIZE_PATH + (pendingAuthorizationRequest() ?? '') },
      });
    }
  },
  component: AuthorizePage,
});

function AuthorizePage() {
  const request = useMemo(() => {
    const raw = pendingAuthorizationRequest() ?? window.location.search;
    return raw.length > 1 ? parseAuthorizationRequest(raw) : null;
  }, []);
  const framed = useMemo(() => isFramed(), []);

  // Enmarcada, la pantalla podría estar debajo de un botón señuelo: ni se
  // consulta la petición ni se enseñan los botones.
  if (framed) return <AuthorizeError code="framed" />;
  if (request === null) return <AuthorizeError code="missing" />;
  if (request.duplicated) return <AuthorizeError code="duplicated" />;
  return <Consent query={request.query} />;
}

/** Manda al navegador fuera de la app, al cliente OAuth. */
function leaveTo(url: string): void {
  clearAuthorizationRequest();
  window.location.assign(url);
}

function Consent({ query }: { query: OAuthAuthorizationQuery }) {
  const { user } = useAuth();
  const [leaving, setLeaving] = useState<string | null>(null);

  const requestQuery = useQuery({
    queryKey: ['oauth', 'authorize', query],
    queryFn: () => api.oauth.getAuthorizationRequest(query),
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'deny') => api.oauth.decide(decisionBody(query, decision)),
    onSuccess: (outcome) => {
      if (outcome.ok) {
        setLeaving(outcome.value.redirectTo);
        leaveTo(outcome.value.redirectTo);
      }
    },
  });

  // Un fallo que el cliente tiene que conocer (sin PKCE, scope desconocido...)
  // vuelve a él con el error, como manda OAuth.
  const rejection =
    requestQuery.data?.ok === false
      ? requestQuery.data.rejection
      : decide.data?.ok === false
        ? decide.data.rejection
        : null;
  const rejectionTarget = rejection?.redirectTo ?? null;
  useEffect(() => {
    if (rejectionTarget !== null) leaveTo(rejectionTarget);
  }, [rejectionTarget]);

  const target = leaving ?? rejectionTarget;
  if (target !== null) {
    let host: string | null = null;
    try {
      host = new URL(target).host;
    } catch {
      host = null;
    }
    return (
      <Shell>
        <div className="flex items-center justify-center gap-3 text-ink-muted" role="status">
          <Spinner size="sm" />
          <span>{host ? OAUTH.redirecting(host) : OAUTH.redirectingToClient}</span>
        </div>
      </Shell>
    );
  }

  if (rejection !== null && rejection.redirectTo === null) {
    return <AuthorizeError code={rejection.error} />;
  }

  if (requestQuery.isPending) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-3 text-ink-muted" role="status">
          <Spinner size="sm" />
          <span>{OAUTH.loading}</span>
        </div>
      </Shell>
    );
  }

  if (requestQuery.isError) {
    return (
      <Shell>
        <ErrorBanner
          title={OAUTH.loadError}
          error={requestQuery.error}
          onRetry={() => void requestQuery.refetch()}
        />
      </Shell>
    );
  }
  // Las respuestas rechazadas ya han salido arriba, con o sin redirección.
  if (!requestQuery.data.ok) return null;

  const info = requestQuery.data.value;

  return (
    <Shell>
      <Card>
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 size-6 shrink-0 text-brand" aria-hidden="true" />
          <h2 className="min-w-0 text-lg font-semibold break-words">
            <strong className="text-brand">{info.clientName}</strong>
            {OAUTH.requestSuffix}
          </h2>
        </div>

        {user ? (
          <p className="mt-3 text-sm break-words text-ink-muted">
            {OAUTH.signedInAs} <strong className="text-ink">{user.displayName}</strong> (
            {user.email}).
          </p>
        ) : null}

        <div className="mt-4 rounded-xl border border-line bg-surface p-3 text-sm">
          <p className="flex flex-wrap items-center gap-x-1.5">
            <ExternalLink className="size-4 shrink-0 text-ink-muted" aria-hidden="true" />
            <span className="text-ink-muted">{OAUTH.returnTo}</span>
            <strong className="font-mono break-all text-ink">{info.redirectHost}</strong>
          </p>
          <p className="mt-1 text-ink-faint">{OAUTH.returnHint}</p>
        </div>

        <h3 className="mt-4 text-sm font-semibold text-ink">{OAUTH.permissionsTitle}</h3>
        <p className="mt-1 text-sm text-ink-muted">{OAUTH.permissions}</p>
        <p className="mt-3 text-sm text-ink-faint">{OAUTH.revokeHint}</p>

        {decide.isError ? (
          <ErrorBanner className="mt-4" title={OAUTH.decisionError} error={decide.error} />
        ) : null}

        <div className="mt-5 flex flex-col gap-2">
          <Button
            size="lg"
            block
            loading={decide.isPending && decide.variables === 'approve'}
            disabled={decide.isPending}
            onClick={() => decide.mutate('approve')}
          >
            {OAUTH.approve}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            block
            loading={decide.isPending && decide.variables === 'deny'}
            disabled={decide.isPending}
            onClick={() => decide.mutate('deny')}
          >
            {OAUTH.deny}
          </Button>
        </div>
      </Card>
    </Shell>
  );
}

/** Error que no se devuelve al cliente: ni el cliente ni la vuelta son de fiar. */
function AuthorizeError({ code }: { code: string }) {
  return (
    <Shell>
      <Card>
        <div className="flex items-start gap-3">
          <TriangleAlert className="mt-0.5 size-6 shrink-0 text-danger" aria-hidden="true" />
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">{OAUTH.errorTitle}</h2>
            <p className="mt-2 text-sm text-ink-muted">{oauthErrorMessage(code)}</p>
          </div>
        </div>
        <a
          href={HOME_ROUTE}
          onClick={() => clearAuthorizationRequest()}
          className="mt-5 inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-line bg-surface-raised px-4 font-semibold text-ink transition-colors hover:bg-surface-overlay"
        >
          {OAUTH.errorHome}
        </a>
      </Card>
    </Shell>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-app flex-col justify-center gap-6 px-4 py-6 xs:px-6">
      <header className="text-center">
        <img src="/icons/icon-192.png" alt="" className="mx-auto size-14 rounded-2xl" />
        <h1 className="mt-3 text-xl font-bold">{OAUTH.pageTitle}</h1>
      </header>
      {children}
    </main>
  );
}
